import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import {
  providerErrorReason,
  trackConnectorConnectFailed,
} from '@/lib/analytics/connectionTracking'
import { type NotionAuthState, NOTION_AUTH_STATE_STORAGE_KEY } from '@/lib/auth/notion'
import { safeParseRnd } from '@/lib/auth/oauth-state'

// Notion OAuth callback. Notion redirects here with `?code=…&state=…`; we validate state and
// hand the code to the backend, which exchanges it for a token and stores it encrypted.
export function AuthNotionPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: connectNotion } = trpc.brandConnection.connectNotion.useMutation()

  const isRunning = useRef(false)
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const error = searchParams.get('error')

  useEffect(() => {
    if (isRunning.current) return
    isRunning.current = true

    const storedState = JSON.parse(
      localStorage.getItem(NOTION_AUTH_STATE_STORAGE_KEY) || 'null',
    ) as NotionAuthState | null
    const referrerPath = storedState?.referrerPath ?? '/settings/connections'

    // Required, not checked-only-when-present: a crafted /auth/notion?code=… link with no
    // state would otherwise connect the attacker's own Notion workspace to whichever brand
    // the victim is signed into, and every post grounded in it afterwards.
    if (!state || !storedState || storedState.rnd !== safeParseRnd(state)) {
      // An `?error=…` without state is the user cancelling or Notion failing, not a forged
      // callback. Nothing gets connected on this path either way.
      const reason = !state && error ? providerErrorReason(error) : 'state_mismatch'
      trackConnectorConnectFailed('notion', reason, error ?? undefined)
      if (reason === 'cancelled') toast('Notion connection cancelled')
      else toast.error('Notion connection failed', { description: 'Security check did not match.' })
      localStorage.removeItem(NOTION_AUTH_STATE_STORAGE_KEY)
      isRunning.current = false
      navigate(referrerPath)
      return
    }

    if (error || !code) {
      const reason = error ? providerErrorReason(error) : 'missing_code'
      trackConnectorConnectFailed('notion', reason, error ?? undefined)
      // Backing out of the consent screen is a choice, not a failure worth a red toast.
      if (reason === 'cancelled') toast('Notion connection cancelled')
      else toast.error('Notion connection failed', { description: error ?? undefined })
      localStorage.removeItem(NOTION_AUTH_STATE_STORAGE_KEY)
      isRunning.current = false
      navigate(referrerPath)
      return
    }

    connectNotion({ code, redirectUri: `${window.location.origin}/auth/notion` })
      .then((res) => {
        toast.success('Notion connected', { description: res.workspaceName ?? undefined })
        navigate(referrerPath)
      })
      .catch((e: unknown) => {
        trackConnectorConnectFailed('notion', 'authorize_failed', e)
        toast.error('Failed to connect Notion', {
          description: e instanceof Error ? e.message : undefined,
        })
        navigate(referrerPath)
      })
      .finally(() => {
        localStorage.removeItem(NOTION_AUTH_STATE_STORAGE_KEY)
        isRunning.current = false
      })
  }, [code, state, error, connectNotion, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting Notion...</p>
      </div>
    </div>
  )
}
